<script setup lang="ts">
import type { NuxtError } from 'nuxt/app';
import type { EngineError } from '#layers/base/public';
import { useOverlay } from '@nuxt/ui/composables';
import { computed, defineAsyncComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { useHead } from '#app/composables/head';
import { getErrorStatusCode, hasErrorCode, isEngineError, isEnginePanicError } from '#layers/base/public';
import { useTranslationUtils } from '#layers/i18n/public';
import { useErrorFormatter } from '#layers/ui/composables/use-error-formatter';

const props = defineProps<{
	error?: Error | NuxtError & { statusCode?: number; statusMessage?: string } | EngineError;
	clearError?: () => void;
}>();

const { t } = useI18n();
const { translateString } = useTranslationUtils();
const { formatError } = useErrorFormatter();
const { showDetails } = useShowDetails();
const showClear = computed(() => import.meta.dev && !!props.clearError);

const statusCode = computed(() => getErrorStatusCode(props.error));
const isWorkspaceAccessDenied = computed(() => props.error && hasErrorCode(props.error, 'WORKSPACE_ACCESS_DENIED'));

const title = computed(() => {
	if (!props.error) {
		return null;
	}
	else if (isEnginePanicError(props.error)) {
		return 'Engine Panic';
	}
	else if ('statusMessage' in props.error && props.error.statusMessage) {
		return translateString(props.error.statusMessage);
	}
	else if (isWorkspaceAccessDenied.value) {
		return t('workspace.no_access');
	}
	else if (statusCode.value === 404) {
		return t('errors.page_not_found');
	}
	else {
		return formatError(props.error);
	}
});

const description = computed(() => {
	if (!props.error) {
		return null;
	}

	if (isWorkspaceAccessDenied.value) {
		return t('workspace.no_access_description');
	}

	if (statusCode.value === 404 && !('message' in props.error && props.error.message)) {
		return t('errors.page_not_found_description');
	}

	const formattedMessage = formatError(props.error);

	// if the error has a message that is different from the title, render the message
	if (formattedMessage !== title.value) {
		return formattedMessage;
	}

	return null;
});

const LazyIllustration404 = defineAsyncComponent(() => import('./illustrations/404.svg'));
const LazyIllustrationError = defineAsyncComponent(() => import('./illustrations/error.svg'));
const LazyIllustrationPanic = defineAsyncComponent(() => import('./illustrations/panic.svg'));
const LazyErrorDetailDialog = defineAsyncComponent(() => import('./ErrorDetailDialog.vue'));

useHead({
	title,
});

function useShowDetails() {
	const overlay = useOverlay();

	return {
		showDetails,
	};

	function showDetails() {
		overlay.create(LazyErrorDetailDialog, {
			props: {
				error: props.error,
			},
		}).open();
	}
}
</script>

<template>
	<DMessageScreen :title :description data-testid="error-screen">
		<template #illustration>
			<LazyIllustration404 v-if="isWorkspaceAccessDenied || statusCode === 404" class="w-44" aria-hidden="true" />
			<LazyIllustrationPanic v-else-if="isEnginePanicError(error)" class="w-32" aria-hidden="true" />
			<LazyIllustrationError v-else class="w-32" aria-hidden="true" />
		</template>

		<DButton
			v-if="isEngineError(error)"
			variant="link"
			color="neutral"
			size="sm"
			:label="$t('common.view_details')"
			@click="showDetails"
		/>

		<template v-if="showClear || !!$slots.default" #actions>
			<DButton v-if="showClear" size="sm" :label="$t('common.clear_error')" @click="clearError" />
			<slot />
		</template>
	</DMessageScreen>
</template>
